import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Typography,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { SmartToy, FlashOn, PersonSearch, SportsEsports } from "@mui/icons-material";
import { Me } from "../lichess/LichessAuth";

interface NewGameTabProps {
  me?: Me | null;
  onGameCreated: () => void;
}

const TIME_PRESETS = [
  { label: "1 min (Bullet)", time: 1, increment: 0, speed: "Bullet" },
  { label: "3 min (Blitz)", time: 3, increment: 0, speed: "Blitz" },
  { label: "3+2 (Blitz)", time: 3, increment: 2, speed: "Blitz" },
  { label: "5 min (Blitz)", time: 5, increment: 0, speed: "Blitz" },
  { label: "5+3 (Blitz)", time: 5, increment: 3, speed: "Blitz" },
  { label: "10 min (Rapid)", time: 10, increment: 0, speed: "Rapid" },
  { label: "15+10 (Rapid)", time: 15, increment: 10, speed: "Rapid" },
];

export const NewGameTab: React.FC<NewGameTabProps> = ({ me, onGameCreated }) => {
  // Seek State
  const [selectedPresetIndex, setSelectedPresetIndex] = useState<number>(2); // 3+2 Blitz default
  const [rated, setRated] = useState<boolean>(false);
  const [color, setColor] = useState<"white" | "black" | "random">("random");
  const [isSeeking, setIsSeeking] = useState<boolean>(false);

  // AI Challenge State
  const [aiLevel, setAiLevel] = useState<number>(1);
  const [aiTimeSeconds, setAiTimeSeconds] = useState<number>(180);
  const [aiIncrement, setAiIncrement] = useState<number>(2);
  const [isChallengingAi, setIsChallengingAi] = useState<boolean>(false);

  const [feedback, setFeedback] = useState<{ type: "success" | "error" | "info"; message: string } | null>(null);

  const handleSeek = async () => {
    if (!me) return;
    setIsSeeking(true);
    setFeedback(null);
    try {
      const preset = TIME_PRESETS[selectedPresetIndex];
      const res = await me.lichessClient.seekGame(
        rated,
        "standard",
        preset.time,
        preset.increment,
        color
      );
      if (res) {
        setFeedback({
          type: "info",
          message: "Game seek submitted to Lichess! Refreshing active games...",
        });
        setTimeout(() => {
          onGameCreated();
        }, 1200);
      }
    } catch (err: any) {
      console.error("Seek error:", err);
      setFeedback({
        type: "error",
        message: `Failed to seek game: ${err.message || "Unknown error"}`,
      });
    } finally {
      setIsSeeking(false);
    }
  };

  const handleChallengeAi = async () => {
    if (!me) return;
    setIsChallengingAi(true);
    setFeedback(null);
    try {
      const res = await me.lichessClient.challengeAi(aiLevel, aiTimeSeconds, aiIncrement);
      if (res.ok) {
        setFeedback({
          type: "success",
          message: `AI Level ${aiLevel} challenge started! Loading game...`,
        });
        setTimeout(() => {
          onGameCreated();
        }, 1000);
      } else {
        const errorText = await res.text().catch(() => "");
        setFeedback({
          type: "error",
          message: `AI Challenge failed (${res.status}): ${errorText}`,
        });
      }
    } catch (err: any) {
      console.error("Challenge AI error:", err);
      setFeedback({
        type: "error",
        message: `Failed to challenge AI: ${err.message || "Unknown error"}`,
      });
    } finally {
      setIsChallengingAi(false);
    }
  };

  if (!me) {
    return (
      <Paper
        sx={{
          p: 4,
          textAlign: "center",
          backgroundColor: "#22272e",
          color: "white",
          borderRadius: 3,
          maxWidth: 600,
          mx: "auto",
        }}
      >
        <SportsEsports sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Lichess Account Required
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please connect your Lichess account to create and join new games.
        </Typography>
        <Button variant="contained" color="primary" href="/connect">
          Connect Lichess
        </Button>
      </Paper>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto" }}>
      {feedback && (
        <Alert
          severity={feedback.type}
          sx={{ mb: 3 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Quick Pairing / Seek Online Game */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              backgroundColor: "#22272e",
              color: "white",
              borderRadius: 3,
              border: "1px solid rgba(255, 255, 255, 0.1)",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardHeader
              avatar={<PersonSearch sx={{ color: "primary.main", fontSize: 32 }} />}
              title={
                <Typography variant="h6" fontWeight="bold">
                  Quick Pairing / Seek Game
                </Typography>
              }
              subheader={
                <Typography variant="caption" color="text.secondary">
                  Find a live opponent on Lichess
                </Typography>
              }
            />
            <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, flexGrow: 1 }}>
              {/* Time Control Preset */}
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: "text.secondary" }}>Time Control</InputLabel>
                <Select
                  value={selectedPresetIndex}
                  label="Time Control"
                  onChange={(e) => setSelectedPresetIndex(Number(e.target.value))}
                  sx={{ color: "white" }}
                >
                  {TIME_PRESETS.map((preset, index) => (
                    <MenuItem key={preset.label} value={index}>
                      {preset.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Color Selection */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Choose Color
                </Typography>
                <ToggleButtonGroup
                  value={color}
                  exclusive
                  onChange={(_, val) => val && setColor(val)}
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="white" sx={{ color: "white", "&.Mui-selected": { bgcolor: "primary.main", color: "white" } }}>
                    ⚪ White
                  </ToggleButton>
                  <ToggleButton value="random" sx={{ color: "white", "&.Mui-selected": { bgcolor: "primary.main", color: "white" } }}>
                    🎲 Random
                  </ToggleButton>
                  <ToggleButton value="black" sx={{ color: "white", "&.Mui-selected": { bgcolor: "primary.main", color: "white" } }}>
                    ⚫ Black
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Rated Toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={rated}
                    onChange={(e) => setRated(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    {rated ? "Rated Game" : "Casual Game (Unrated)"}
                  </Typography>
                }
              />

              {/* Seek Button */}
              <Box mt="auto" pt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  disabled={isSeeking}
                  startIcon={isSeeking ? <CircularProgress size={20} color="inherit" /> : <FlashOn />}
                  onClick={handleSeek}
                  sx={{ textTransform: "none", fontWeight: "bold", py: 1.2 }}
                >
                  {isSeeking ? "Seeking Opponent..." : "Seek Game"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Play with Computer / AI */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              backgroundColor: "#22272e",
              color: "white",
              borderRadius: 3,
              border: "1px solid rgba(255, 255, 255, 0.1)",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardHeader
              avatar={<SmartToy sx={{ color: "secondary.main", fontSize: 32 }} />}
              title={
                <Typography variant="h6" fontWeight="bold">
                  Play vs Computer (Stockfish)
                </Typography>
              }
              subheader={
                <Typography variant="caption" color="text.secondary">
                  Practice offline or against Lichess bot
                </Typography>
              }
            />
            <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, flexGrow: 1 }}>
              {/* Bot Difficulty Level */}
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: "text.secondary" }}>Bot Level</InputLabel>
                <Select
                  value={aiLevel}
                  label="Bot Level"
                  onChange={(e) => setAiLevel(Number(e.target.value))}
                  sx={{ color: "white" }}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((lvl) => (
                    <MenuItem key={lvl} value={lvl}>
                      Level {lvl} {lvl === 1 ? "(Beginner)" : lvl === 4 ? "(Intermediate)" : lvl === 8 ? "(Master)" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Time Control */}
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: "text.secondary" }}>Time Limit</InputLabel>
                <Select
                  value={aiTimeSeconds}
                  label="Time Limit"
                  onChange={(e) => setAiTimeSeconds(Number(e.target.value))}
                  sx={{ color: "white" }}
                >
                  <MenuItem value={60}>1 min</MenuItem>
                  <MenuItem value={180}>3 min</MenuItem>
                  <MenuItem value={300}>5 min</MenuItem>
                  <MenuItem value={600}>10 min</MenuItem>
                  <MenuItem value={900}>15 min</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: "text.secondary" }}>Increment (seconds)</InputLabel>
                <Select
                  value={aiIncrement}
                  label="Increment (seconds)"
                  onChange={(e) => setAiIncrement(Number(e.target.value))}
                  sx={{ color: "white" }}
                >
                  <MenuItem value={0}>0 seconds</MenuItem>
                  <MenuItem value={1}>1 second</MenuItem>
                  <MenuItem value={2}>2 seconds</MenuItem>
                  <MenuItem value={5}>5 seconds</MenuItem>
                </Select>
              </FormControl>

              {/* Challenge Button */}
              <Box mt="auto" pt={2}>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  size="large"
                  disabled={isChallengingAi}
                  startIcon={isChallengingAi ? <CircularProgress size={20} color="inherit" /> : <SmartToy />}
                  onClick={handleChallengeAi}
                  sx={{ textTransform: "none", fontWeight: "bold", py: 1.2 }}
                >
                  {isChallengingAi ? "Starting Game..." : "Play with AI"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NewGameTab;
